/**
 * ErioAssessmentAddon — P3 cross-app capability. On the lite-assessment result
 * screen, lets the visitor pull a partner-grade risk register check or
 * improvement plan from the sister platform (ERIO), powered by their own lite
 * answers. Ekselens proxies the call server-side. If ERIO is unavailable the
 * card quietly shows an unavailable state.
 */
import React, { useState } from "react";
import axios from "axios";
import { ShieldAlert, ListChecks, Loader2, ArrowUpRight, Sparkles } from "lucide-react";
import { erioDeepLink } from "../lib/ssoSession";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ErioAssessmentAddon({ answers, subBrand, overall, slug }) {
  const [loading, setLoading] = useState(null); // "risk" | "plan" | null
  const [result, setResult] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  const run = async (type) => {
    setLoading(type);
    setUnavailable(false);
    try {
      const { data } = await axios.post(`${API_BASE}/partner/erio-assessment`, {
        type,
        answers: answers || {},
        sub_brand: subBrand,
        overall,
        slug,
      });
      if (data?.available) setResult({ ...data, type });
      else setUnavailable(true);
    } catch {
      setUnavailable(true);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="mt-12 border border-[#7737F6]/30 bg-[#7737F6]/[0.04] p-8" data-testid="erio-addon">
      <div className="flex items-center gap-2 text-[11px] font-medium tracking-[0.18em] text-[#7737F6] uppercase mb-3">
        <Sparkles size={13} /> Partner add-on · ERIO
      </div>
      <h3 className="ek-heading text-xl md:text-2xl text-black">Go deeper with the ERIO platform</h3>
      <p className="ek-body text-[14px] mt-2 max-w-2xl">
        Run your {subBrand} answers through ERIO — our sister platform — for a partner-grade risk
        register check or an improvement plan, in seconds.
      </p>

      {!result && (
        <div className="flex flex-wrap gap-3 mt-5">
          <button
            type="button"
            onClick={() => run("risk")}
            disabled={loading !== null}
            className="ek-btn ek-btn-primary text-[13px] inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="erio-addon-risk"
          >
            {loading === "risk" ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
            Run ERIO risk check
          </button>
          <button
            type="button"
            onClick={() => run("plan")}
            disabled={loading !== null}
            className="ek-btn ek-btn-outline-dark text-[13px] inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="erio-addon-plan"
          >
            {loading === "plan" ? <Loader2 size={14} className="animate-spin" /> : <ListChecks size={14} />}
            Generate improvement plan
          </button>
        </div>
      )}

      {unavailable && (
        <p className="text-[13px] text-zinc-500 mt-4" data-testid="erio-addon-unavailable">
          The ERIO add-on is temporarily unavailable. Please try again shortly.
        </p>
      )}

      {result && (
        <div className="mt-6 bg-white border border-zinc-200 p-5" data-testid="erio-addon-result">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase">
              ERIO {result.type === "plan" ? "improvement plan" : "risk check"}
            </div>
            {typeof result.score === "number" && (
              <div className="text-[13px]">Score <strong className="text-[#7737F6]">{result.score}</strong></div>
            )}
          </div>
          <p className="text-[14px] text-black mt-3 leading-relaxed" data-testid="erio-addon-summary">{result.summary}</p>
          {result.recommendations?.length ? (
            <ul className="mt-4 space-y-2">
              {result.recommendations.map((r, i) => (
                <li key={i} className="text-[13px] text-zinc-700 border-l-2 border-[#7737F6] pl-3">{r}</li>
              ))}
            </ul>
          ) : null}
          {result.url && (
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[13px] text-[#7737F6] hover:underline mt-5 font-medium"
              data-testid="erio-addon-open"
            >
              Open the full ERIO report <ArrowUpRight size={13} />
            </a>
          )}
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-[#7737F6]/15">
        <a
          href={erioDeepLink(`/?source=ekselens-lite-${slug || subBrand || "assessment"}`)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[12.5px] text-[#7737F6] hover:underline font-medium"
          data-testid="erio-addon-explore"
        >
          Or explore the ERIO platform directly <ArrowUpRight size={12} />
        </a>
      </div>
    </div>
  );
}
